import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    ArrowLeftOutlined,
    ControlOutlined,
    ReloadOutlined,
    LoadingOutlined,
    DeleteOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import styles from '@/styles/pages/projects/ValheimPage.module.css';
import {projectsAPI} from '@/api/endpoints/projects.js';

const STATE_LABELS = {online: 'Онлайн', starting: 'Запускается', offline: 'Выключен'};

// Аватар, ник (если есть) и SteamID игрока
const SteamUser = ({id, profile, extra}) => (
    <span className={styles.user}>
        {profile?.avatar
            ? <img className={styles.avatar} src={profile.avatar} alt="" loading="lazy"/>
            : <span className={styles.avatar}/>}
        <span>
            <b>{profile?.name || extra || '—'}</b>
            <span className={`${styles.mono} ${styles.muted}`}> {id}</span>
        </span>
    </span>
);

// SteamID64 или ссылка на профиль Steam
const SteamList = ({title, ids, profiles, onAdd, onRemove, addLabel, busy}) => {
    const [value, setValue] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        if (!value.trim()) return;
        if (await onAdd(value.trim())) setValue('');
    };

    return (
        <section className={styles.card}>
            <h2 className={styles.cardTitle}>{title} <span className={styles.count}>{ids.length}</span></h2>
            <form className={styles.addRow} onSubmit={submit}>
                <input className={styles.input} value={value} onChange={(e) => setValue(e.target.value)}
                       placeholder="SteamID64 или ссылка на профиль Steam"/>
                <button className={styles.button} type="submit" disabled={busy || !value.trim()}>
                    <PlusOutlined/> {addLabel}
                </button>
            </form>
            {ids.length === 0 && <p className={styles.muted}>Список пуст</p>}
            <ul className={styles.list}>
                {ids.map((id) => (
                    <li key={id} className={styles.listItem}>
                        <SteamUser id={id} profile={profiles[id]}/>
                        <button className={`${styles.iconButton} ${styles.danger}`} title="Удалить"
                                disabled={busy} onClick={() => onRemove(id)}>
                            <DeleteOutlined/>
                        </button>
                    </li>
                ))}
            </ul>
        </section>
    );
};

/**
 * /projects/:uuid/valheim — управление Valheim-сервером через его API
 * (запросы проксирует бэкенд: admin_url и токен берутся из проекта).
 */
const ValheimPage = () => {
    const navigate = useNavigate();
    const {uuid} = useParams();
    const [status, setStatus] = useState(null);
    const [online, setOnline] = useState([]);
    const [whitelist, setWhitelist] = useState([]);
    const [bans, setBans] = useState([]);
    const [backup, setBackup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [profiles, setProfiles] = useState({});
    const [kickSteam, setKickSteam] = useState('');

    const call = (method, path, data) => projectsAPI.valheim(uuid, method, path, data);

    // Ники и аватарки: не критично, при ошибке остаются только SteamID
    const loadProfiles = async (ids) => {
        const unique = [...new Set(ids)].slice(0, 100);
        if (unique.length === 0) return;
        const response = await projectsAPI.steamProfiles(unique);
        if (response.success) setProfiles((prev) => ({...prev, ...response.data}));
    };

    const load = useCallback(async () => {
        setLoading(true);
        const [st, on, wl, bn, bk] = await Promise.all([
            call('GET', 'status'), call('GET', 'online'), call('GET', 'whitelist'),
            call('GET', 'bans'), call('GET', 'backup'),
        ]);
        if (!st.success) {
            setError(st.message || 'Сервер управления недоступен');
        } else {
            setError('');
            setStatus(st.data);
            setOnline(on.data?.players || []);
            setWhitelist(wl.data?.steamids || []);
            setBans(bn.data?.steamids || []);
            setBackup(bk.data || null);
            loadProfiles([...(on.data?.players || []).map((p) => p.steamid),
                ...(wl.data?.steamids || []), ...(bn.data?.steamids || [])]);
        }
        setLoading(false);
    }, [uuid]);

    useEffect(() => {
        document.title = 'Управление Valheim | DevSpace';
        load();
    }, [load]);

    // Выполнить действие, показать результат и обновить данные
    const run = async (method, path, data, okMessage) => {
        setBusy(true);
        setMessage('');
        const response = await call(method, path, data);
        setBusy(false);
        if (!response.success) {
            setError(response.message || 'Ошибка запроса');
            return false;
        }
        setError('');
        setMessage(okMessage);
        load();
        return true;
    };

    const handleRestart = () => {
        if (window.confirm('Перезапустить сервер? Мир сохранится, игроки будут отключены.')) {
            run('POST', 'restart', undefined, 'Перезапуск запущен');
        }
    };

    const handleKick = async (steam) => {
        if (await run('POST', 'kick', {steam}, 'Кик поставлен в очередь')) setKickSteam('');
    };

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.header}>
                    <button className={styles.backButton} onClick={() => navigate(-1)}>
                        <ArrowLeftOutlined/> Назад
                    </button>
                    <h1 className={styles.title}><ControlOutlined/> Управление сервером</h1>
                    <button className={styles.backButton} onClick={load} disabled={loading}>
                        <ReloadOutlined spin={loading}/> Обновить
                    </button>
                </div>

                {error && <div className={`${styles.notice} ${styles.errorNotice}`}>{error}</div>}
                {message && <div className={styles.notice}>{message}</div>}
                {loading && !status && <div className={styles.notice}><LoadingOutlined/> Загрузка...</div>}

                {status && (
                    <>
                        <section className={styles.card}>
                            <h2 className={styles.cardTitle}>Статус</h2>
                            <div className={styles.statusRow}>
                                <span className={`${styles.badge} ${styles[status.state] || ''}`}>
                                    {STATE_LABELS[status.state] || status.state}
                                </span>
                                <span>Игроков: {status.players}</span>
                                {status.restarting && <span className={styles.muted}>Идёт перезапуск...</span>}
                                <button className={`${styles.button} ${styles.dangerButton}`} disabled={busy || status.restarting}
                                        onClick={handleRestart}>
                                    Перезапустить
                                </button>
                            </div>
                        </section>

                        <section className={styles.card}>
                            <h2 className={styles.cardTitle}>В игре <span className={styles.count}>{online.length}</span></h2>
                            {online.length === 0 && <p className={styles.muted}>Никого нет</p>}
                            <ul className={styles.list}>
                                {online.map((p) => (
                                    <li key={p.steamid} className={styles.listItem}>
                                        <span>
                                            <SteamUser id={p.steamid} profile={profiles[p.steamid]} extra={p.character}/>
                                            {p.character && profiles[p.steamid]?.name && (
                                                <span className={styles.muted}> · {p.character}</span>
                                            )}
                                            {p.telegram && <span className={styles.muted}> · {p.telegram}</span>}
                                        </span>
                                        <span className={styles.actions}>
                                            <button className={styles.button} disabled={busy} onClick={() => handleKick(p.steamid)}>
                                                Кик
                                            </button>
                                            <button className={`${styles.button} ${styles.dangerButton}`} disabled={busy}
                                                    onClick={() => window.confirm(`Забанить ${p.character || p.steamid}?`)
                                                        && run('POST', 'bans', {steam: p.steamid}, 'Игрок забанен')}>
                                                Бан
                                            </button>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <form className={styles.addRow} onSubmit={(e) => {
                                e.preventDefault();
                                if (kickSteam.trim()) handleKick(kickSteam.trim());
                            }}>
                                <input className={styles.input} value={kickSteam} onChange={(e) => setKickSteam(e.target.value)}
                                       placeholder="Кикнуть по SteamID64 или ссылке"/>
                                <button className={styles.button} type="submit" disabled={busy || !kickSteam.trim()}>Кик</button>
                            </form>
                        </section>

                        <div className={styles.columns}>
                            <SteamList title="Whitelist" ids={whitelist} profiles={profiles} busy={busy} addLabel="Добавить"
                                       onAdd={(steam) => run('POST', 'whitelist', {steam}, 'Добавлено в whitelist')}
                                       onRemove={(id) => run('DELETE', `whitelist/${encodeURIComponent(id)}`, undefined, 'Удалено из whitelist')}/>
                            <SteamList title="Баны" ids={bans} profiles={profiles} busy={busy} addLabel="Забанить"
                                       onAdd={(steam) => run('POST', 'bans', {steam}, 'Игрок забанен')}
                                       onRemove={(id) => run('DELETE', `bans/${encodeURIComponent(id)}`, undefined, 'Игрок разбанен')}/>
                        </div>

                        <section className={styles.card}>
                            <h2 className={styles.cardTitle}>Офсайт-бэкап</h2>
                            <div className={styles.statusRow}>
                                <button className={styles.button} disabled={busy || backup?.running}
                                        onClick={() => run('POST', 'backup', undefined, 'Бэкап запущен')}>
                                    {backup?.running ? 'Идёт бэкап...' : 'Запустить бэкап'}
                                </button>
                                {backup?.last && (
                                    <span className={backup.last.ok ? undefined : styles.errorText}>
                                        Последний: {backup.last.ok ? 'успешно' : 'ошибка'}
                                    </span>
                                )}
                            </div>
                            {backup?.last?.output && <pre className={styles.output}>{backup.last.output}</pre>}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

export default ValheimPage;
